import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Field = ({ id, label, testId, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="font-type text-xs uppercase tracking-wider text-[#1B2A41]">
      {label}
    </Label>
    <Input id={id} data-testid={testId} className="h-11 bg-white border-[#D8D2C4] focus-visible:ring-[#E2705A]" {...props} />
  </div>
);

export const JoinForm = ({ preferredElectorate }) => {
  const qc = useQueryClient();
  const { signIn } = useMember();
  const next = new URLSearchParams(window.location.search).get("next");
  const [form, setForm] = useState({ name: "", email: "", postcode: "" });
  const [options, setOptions] = useState(null);
  const [electorate, setElectorate] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (options && preferredElectorate && options.some((o) => o.name === preferredElectorate)) setElectorate(preferredElectorate);
  }, [options, preferredElectorate]);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (k === "postcode") {
      setOptions(null);
      setElectorate("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, postcode: form.postcode.trim() };
      if (options) payload.electorate = electorate;
      else if (preferredElectorate) payload.electorate = undefined;
      const { data } = await api.post("/register", payload);
      if (data.needs_selection) {
        setOptions(data.electorates);
        if (preferredElectorate && data.electorates.some((o) => o.name === preferredElectorate)) setElectorate(preferredElectorate);
        toast.info("Your postcode spans several electorates. Please pick yours.");
      } else {
        setDone(data);
        if (data.token) signIn(data.token, data.member);
        qc.invalidateQueries({ queryKey: ["electorates"] });
        qc.invalidateQueries({ queryKey: ["electorate", data.member.electorate] });
        toast.success(`Welcome aboard, ${data.member.name.split(" ")[0]}!`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="join-success" className="text-center py-6">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#5A7355]" />
        <h3 className="font-type font-bold text-2xl text-[#1B2A41] mt-4">You're a F.E.R.M</h3>
        <p className="mt-2 text-sm text-[#1E2830]/80">
          <strong>{done.member.name}</strong> is now in <strong>Group {done.group_number}</strong> for the electorate of{" "}
          <strong data-testid="join-success-electorate">{done.member.electorate}</strong> ({done.member.state}).
        </p>
        {done.member.member_number && (
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1B2A41] text-[#FDFBF7] px-4 h-9 font-type text-sm" data-testid="join-success-member-number">
            Member no. <strong className="text-[#E2705A]">{done.member.member_number}</strong>
          </p>
        )}
        <p className="mt-4 text-xs text-[#5C6A79]">Your welcome email is on its way — it includes your sitting member's details and your first task.</p>
        {next === "/write" && (
          <Link to="/write" data-testid="join-success-write-letter" className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-white text-xs font-type font-bold uppercase tracking-wider">Write to your federal member <ArrowRight className="w-4 h-4" /></Link>
        )}
        <button
          type="button"
          data-testid="join-another-button"
          onClick={() => {
            setDone(null);
            setForm({ name: "", email: "", postcode: "" });
            setOptions(null);
          }}
          className="mt-6 text-sm font-semibold text-[#E2705A] hover:underline"
        >
          Register another person
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="join-registration-form" className="space-y-4">
      <Field id="name" label="Full name" testId="join-name-input" value={form.name} onChange={set("name")} required minLength={2} placeholder="Jane Citizen" />
      <Field id="email" label="Email" testId="join-email-input" type="email" value={form.email} onChange={set("email")} required placeholder="jane@example.com" />
      <Field id="postcode" label="Postcode" testId="join-postcode-input" inputMode="numeric" pattern="\d{4}" maxLength={4} value={form.postcode} onChange={set("postcode")} required placeholder="2906" />

      {options && (
        <div className="space-y-2" data-testid="electorate-selector-dropdown">
          <p className="font-type text-xs uppercase tracking-wider text-[#1B2A41]">Postcode {form.postcode} spans {options.length} electorates — pick yours</p>
          <div className="grid gap-2">
            {options.map((o) => (
              <label
                key={o.name}
                data-testid={`electorate-option-${o.name}`}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                  electorate === o.name ? "border-[#E2705A] bg-[#E2705A]/10" : "border-[#D8D2C4] bg-white hover:border-[#8FA58A]"
                }`}
              >
                <input type="radio" name="electorate" value={o.name} checked={electorate === o.name} onChange={() => setElectorate(o.name)} className="accent-[#E2705A]" />
                <span className="font-semibold text-sm text-[#1B2A41]">{o.name}</span>
                <span className="text-xs text-[#5C6A79] ml-auto">{o.state}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={busy || (options && !electorate)}
        data-testid="join-submit-button"
        className="w-full h-12 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm transition-colors inline-flex items-center justify-center gap-2"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {options ? "Confirm & Join" : "Sign me up as a F.E.R.M"}
      </button>
      <p className="text-[11px] text-[#5C6A79] text-center">Your email is only visible to R.O.A.N admins. Your name appears on the map for your electorate.</p>
      <p className="text-[11px] text-[#5C6A79] text-center" data-testid="join-legal-notice">
        By signing up you agree to our{" "}
        <a href={`${API}/documents/terms/download`} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#E2705A]">Terms and Conditions</a> and{" "}
        <a href={`${API}/documents/privacy/download`} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#E2705A]">Privacy Policy</a>.
        "The ROAN Initiative" is a registered trading name of the "Average AF Pty Ltd" company.
      </p>
    </form>
  );
};
